import { Temporal } from 'temporal-polyfill';

export type PlainDateInitializer = Temporal.PlainDate | Temporal.PlainDateLike | string;

export class PlainDateInterval {
  static from(start: PlainDateInitializer, end: PlainDateInitializer): PlainDateInterval {
    return new this(Temporal.PlainDate.from(start), Temporal.PlainDate.from(end));
  }

  constructor(
    public start: Temporal.PlainDate,
    public end: Temporal.PlainDate,
  ) {
    assertValidPlainDateIntervalLike(start, end);
  }

  toString(): string {
    return `${this.start.toString()}/${this.end.toString()}`;
  }

  contains(plainDate: Temporal.PlainDate): boolean {
    return (
      Temporal.PlainDate.compare(this.start, plainDate) <= 0 && Temporal.PlainDate.compare(plainDate, this.end) <= 0
    );
  }

  isOverlapping(other: PlainDateInterval): boolean {
    return other.contains(this.start) || other.contains(this.end);
  }

  *plainDates(): IterableIterator<Temporal.PlainDate> {
    for (let day = this.start; Temporal.PlainDate.compare(day, this.end) <= 0; day = day.add({ days: 1 })) {
      yield day;
    }
  }

  *[Symbol.iterator](): IterableIterator<Temporal.PlainDate> {
    yield* this.plainDates();
  }
}

export function assertValidPlainDateIntervalLike(start: Temporal.PlainDate, end: Temporal.PlainDate): void {
  if (Temporal.PlainDate.compare(start, end) > 0) {
    throw new RangeError("A PlainDateInterval's start must occur before the end");
  }
}

/**
 * Determines whether the given plain date interval is valid.
 *
 * @param start start date of the interval
 * @param end end date of the interval
 * @returns true if the interval is valid, false otherwise
 */
export function validatePlainDateIntervalLike(start: Temporal.PlainDate, end: Temporal.PlainDate): boolean {
  try {
    assertValidPlainDateIntervalLike(start, end);
    return true;
  } catch {
    return false;
  }
}

export class PlainDateIntervalSet {
  intervals: PlainDateInterval[];

  constructor(items: Iterable<PlainDateInterval>) {
    this.intervals = [...items];
  }

  toString(): string {
    return this.intervals.map((interval) => interval.toString()).join(',');
  }

  contains(plainDate: Temporal.PlainDate): boolean {
    for (const interval of this.intervals) {
      if (interval.contains(plainDate)) {
        return true;
      }
    }
    return false;
  }
}
